// src/components/ForgotPasswordPage.js
import React, { useState } from 'react';
import SEO from '../components/SEO';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Link,
  Alert,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  alpha,
  InputAdornment,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FavoriteBorder, LockReset, CheckCircleOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import AuthContext from '../context/AuthContext';
import { useContext } from 'react';

function ForgotPasswordPage() {
  const [step, setStep] = useState(1); // 1=email, 2=code, 3=new password, 4=success
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const { backendUrl } = useContext(AuthContext);

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      '&.Mui-focused fieldset': { borderColor: '#7B6B8D' }
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#7B6B8D' }
  };

  const btnSx = {
    borderRadius: 2,
    py: 1.5,
    background: '#7B6B8D',
    boxShadow: 'none',
    '&:hover': { background: '#6A5A7A', boxShadow: 'none' }
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}api/auth/password/reset/request-code/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setStep(2);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send reset code');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError(null);

    if (code.length !== 5) {
      setError('Please enter the 5-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}api/auth/password/reset/verify-code/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });

      const data = await response.json();
      if (response.ok) {
        setResetToken(data.reset_token);
        setStep(3);
      } else {
        throw new Error(data.error || 'Invalid or expired code');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setError(null);
    try {
      await fetch(`${backendUrl}api/auth/password/reset/request-code/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setCode('');
      setError(null);
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}api/auth/password/reset/with-code/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reset_token: resetToken, new_password: password, confirm_password: confirmPassword }),
      });

      if (response.ok) {
        setStep(4);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to reset password');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <SEO title="Forgot Password - Couplendar" />
    <Box sx={{ minHeight: '100vh', background: '#F9F7F4', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="sm">
        {/* Logo */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 4, cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <FavoriteBorder sx={{ fontSize: 40, color: '#C4856A' }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#7B6B8D' }}>
            Couplendar
          </Typography>
        </Box>

        <Card sx={{ background: 'white', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', borderRadius: 3, border: 'none' }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>

            {/* Step 4: Success */}
            {step === 4 ? (
              <Box textAlign="center">
                <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 80, height: 80, borderRadius: '50%', background: alpha('#A8D5BA', 0.1), mb: 3 }}>
                  <CheckCircleOutline sx={{ fontSize: 48, color: '#A8D5BA' }} />
                </Box>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>Password Reset!</Typography>
                <Alert severity="success" sx={{ mb: 3, borderRadius: 2, textAlign: 'left', '& .MuiAlert-icon': { color: '#A8D5BA' } }}>
                  Your password has been reset successfully. You can now log in with your new password.
                </Alert>
                <Button fullWidth variant="contained" onClick={() => navigate('/login')} sx={btnSx}>
                  Go to Sign In
                </Button>
              </Box>

            ) : step === 3 ? (
              /* Step 3: New password */
              <>
                <Box textAlign="center" mb={3}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 64, height: 64, borderRadius: '50%', background: alpha('#7B6B8D', 0.1), mb: 2 }}>
                    <LockReset sx={{ fontSize: 32, color: '#7B6B8D' }} />
                  </Box>
                  <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>Set New Password</Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Enter your new password below.
                  </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

                <Box component="form" onSubmit={handleResetPassword} noValidate>
                  <Stack spacing={2.5}>
                    <TextField
                      label="New Password"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      fullWidth
                      required
                      autoFocus
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    />
                    <TextField
                      label="Confirm New Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      variant="outlined"
                      fullWidth
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    />
                    <Button type="submit" fullWidth variant="contained" disabled={loading} sx={btnSx}>
                      {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Reset Password'}
                    </Button>
                  </Stack>
                </Box>
              </>

            ) : step === 2 ? (
              /* Step 2: Enter code */
              <>
                <Box textAlign="center" mb={3}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 64, height: 64, borderRadius: '50%', background: alpha('#7B6B8D', 0.1), mb: 2 }}>
                    <LockReset sx={{ fontSize: 32, color: '#7B6B8D' }} />
                  </Box>
                  <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>Enter Reset Code</Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    We've sent a 5-digit code to <strong>{email}</strong>. Enter it below.
                  </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

                <Box component="form" onSubmit={handleVerifyCode} noValidate>
                  <Stack spacing={2.5}>
                    <TextField
                      label="5-digit reset code"
                      variant="outlined"
                      fullWidth
                      required
                      autoFocus
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                      inputProps={{ maxLength: 5, inputMode: 'numeric', style: { letterSpacing: '0.5em', fontSize: '1.4rem', textAlign: 'center' } }}
                      sx={inputSx}
                    />
                    <Button type="submit" fullWidth variant="contained" disabled={loading || code.length !== 5} sx={btnSx}>
                      {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Verify Code'}
                    </Button>
                  </Stack>

                  <Box textAlign="center" mt={3}>
                    <Typography variant="body2" sx={{ color: '#9B9B9B' }}>
                      Didn't receive a code?{' '}
                      <Link
                        component="button"
                        type="button"
                        onClick={handleResendCode}
                        disabled={resendLoading}
                        sx={{ color: '#7B6B8D', textDecoration: 'none', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {resendLoading ? 'Sending...' : 'Resend code'}
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              </>

            ) : (
              /* Step 1: Email entry */
              <>
                <Box textAlign="center" mb={3}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 64, height: 64, borderRadius: '50%', background: alpha('#7B6B8D', 0.1), mb: 2 }}>
                    <LockReset sx={{ fontSize: 32, color: '#7B6B8D' }} />
                  </Box>
                  <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>Reset Password</Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Enter your email address and we'll send you a 5-digit reset code.
                  </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

                <Box component="form" onSubmit={handleRequestCode} noValidate>
                  <Stack spacing={2.5}>
                    <TextField
                      label="Email Address"
                      type="email"
                      variant="outlined"
                      fullWidth
                      required
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      sx={inputSx}
                    />
                    <Button type="submit" fullWidth variant="contained" disabled={loading} sx={btnSx}>
                      {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Send Reset Code'}
                    </Button>
                  </Stack>

                  <Box textAlign="center" mt={3}>
                    <Typography variant="body2" sx={{ color: '#9B9B9B' }}>
                      Remember your password?{' '}
                      <Link href="/login" sx={{ color: '#7B6B8D', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                        Sign in
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              </>
            )}

          </CardContent>
        </Card>
      </Container>
    </Box>
    </>
  );
}

export default ForgotPasswordPage;
